<template>
  <div class="space-y-6">
    <!-- Welcome Section -->
    <div class="bg-white shadow-md rounded-lg p-6">
      <h1 class="text-3xl font-bold text-gray-900">Dashboard</h1>
      <p class="text-gray-600 mt-2">Welkom terug, {{ currentUser?.name }}!</p>

      <div v-if="currentUser?.business_name" class="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <p class="text-sm text-blue-600 font-medium">Bedrijf</p>
        <p class="text-xl font-bold text-blue-900">{{ currentUser.business_name }}</p>
        <p v-if="currentUser?.business_domain" class="text-sm text-blue-700 mt-1">{{ currentUser.business_domain }}</p>
      </div>
    </div>

    <!-- Statistics Section -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div class="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div class="flex items-center">
          <div class="flex-1">
            <p class="text-sm font-medium text-blue-600 uppercase">Totaal Tags</p>
            <div v-if="loading" class="flex items-center mt-2">
              <div class="animate-spin rounded-full h-8 w-8 border-4 border-blue-200 border-t-blue-600"></div>
            </div>
            <p v-else class="text-3xl font-bold text-blue-900">{{ tags.length }}</p>
          </div>
          <div class="text-blue-400">
            <svg class="w-12 h-12" fill="currentColor" viewBox="0 0 20 20">
              <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z"></path>
            </svg>
          </div>
        </div>
      </div>

      <div class="bg-green-50 border border-green-200 rounded-lg p-6">
        <div class="flex items-center">
          <div class="flex-1">
            <p class="text-sm font-medium text-green-600 uppercase">Totaal Triggers</p>
            <div v-if="loading" class="flex items-center mt-2">
              <div class="animate-spin rounded-full h-8 w-8 border-4 border-green-200 border-t-green-600"></div>
            </div>
            <p v-else class="text-3xl font-bold text-green-900">{{ totalTriggers }}</p>
          </div>
          <div class="text-green-400">
            <svg class="w-12 h-12" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z" clip-rule="evenodd"></path>
            </svg>
          </div>
        </div>
      </div>

      <div class="bg-purple-50 border border-purple-200 rounded-lg p-6">
        <div class="flex items-center">
          <div class="flex-1">
            <p class="text-sm font-medium text-purple-600 uppercase">Gemiddeld/Tag</p>
            <div v-if="loading" class="flex items-center mt-2">
              <div class="animate-spin rounded-full h-8 w-8 border-4 border-purple-200 border-t-purple-600"></div>
            </div>
            <p v-else class="text-3xl font-bold text-purple-900">{{ averageTriggers }}</p>
          </div>
          <div class="text-purple-400">
            <svg class="w-12 h-12" fill="currentColor" viewBox="0 0 20 20">
              <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z"></path>
            </svg>
          </div>
        </div>
      </div>
    </div>

    <!-- Quick Actions -->
    <div class="bg-white shadow-md rounded-lg p-6">
      <h2 class="text-xl font-bold text-gray-900 mb-4">Snelle acties</h2>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <router-link :to="{ name: 'Tags/new' }" class="flex items-center p-4 bg-blue-50 hover:bg-blue-100 rounded-lg border border-blue-200 transition">
          <svg class="w-8 h-8 text-blue-600 mr-3" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clip-rule="evenodd"></path>
          </svg>
          <div>
            <p class="font-semibold text-gray-900">Nieuwe Tag Aanmaken</p>
            <p class="text-sm text-gray-600">Maak een nieuwe QR-code tag</p>
          </div>
        </router-link>

        <router-link :to="{ name: 'Tags' }" class="flex items-center p-4 bg-green-50 hover:bg-green-100 rounded-lg border border-green-200 transition">
          <svg class="w-8 h-8 text-green-600 mr-3" fill="currentColor" viewBox="0 0 20 20">
            <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z"></path>
            <path fill-rule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clip-rule="evenodd"></path>
          </svg>
          <div>
            <p class="font-semibold text-gray-900">Bekijk Alle Tags</p>
            <p class="text-sm text-gray-600">Beheer je bestaande tags</p>
          </div>
        </router-link>
      </div>
    </div>

    <!-- Tags Overview Section -->
    <div class="bg-white shadow-md rounded-lg p-6">
      <div class="flex justify-between items-center mb-4">
        <h2 class="text-xl font-bold text-gray-900">Recente Tags</h2>
        <router-link :to="{ name: 'Tags' }" class="text-blue-600 hover:text-blue-800 font-semibold text-sm">
          Bekijk alles →
        </router-link>
      </div>

      <div v-if="loading" class="text-center py-8 text-gray-500">
        Tags laden...
      </div>

      <div v-else-if="tags.length === 0" class="text-center py-8">
        <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"></path>
        </svg>
        <p class="mt-2 text-gray-500">Je hebt nog geen tags aangemaakt.</p>
        <router-link :to="{ name: 'Tags/new' }" class="mt-4 inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-semibold text-sm">
          Maak je eerste tag aan
        </router-link>
      </div>

      <ul v-else class="divide-y divide-gray-200">
        <li class="py-4" v-for="tag in recentTags" :key="tag.id">
          <div class="flex flex-col md:flex-row md:items-center p-4 rounded-xl bg-gray-50 hover:bg-gray-100 transition">
            <div class="mr-4">
              <img class="w-20 h-20 rounded" :src="`data:image/png;base64,${tag.qr_code}`" alt="QR Code" />
            </div>
            <div class="flex-1 min-w-0 mt-3 md:mt-0">
              <p class="text-lg font-medium text-gray-900 truncate">
                {{ tag.name }}
              </p>
              <p class="text-sm text-gray-600 mt-1">
                <strong>{{ tag.url }}</strong>
              </p>
              <p class="text-sm text-gray-500 mt-1">
                Target: {{ tag.target_url }}
              </p>
              <a v-if="tag.download_url" :href="tag.download_url" class="inline-flex mt-2 text-sm text-blue-600 font-semibold hover:underline">
                Download QR-codes
              </a>
            </div>
            <div class="flex items-center mt-3 md:mt-0">
              <div class="text-center px-4 py-2 bg-white rounded-lg shadow border border-gray-200">
                <p class="text-2xl font-bold text-gray-900">{{ tag.events_count }}</p>
                <p class="text-xs text-gray-600">Triggers</p>
              </div>
            </div>
          </div>
        </li>
      </ul>

      <div v-if="tags.length > 5" class="mt-4 text-center">
        <router-link :to="{ name: 'Tags' }" class="text-blue-600 hover:text-blue-800 font-semibold">
          Bekijk alle {{ tags.length }} tags →
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
import {mapStores} from "pinia";
import {useAuthStore} from "@/stores/auth.js";
import {useTagsStore} from "@/stores/tags.js";

export default {
  data() {
    return {
    }
  },
  computed: {
    ...mapStores(useAuthStore, useTagsStore),
    currentUser() {
      return this.authStore?.currentUser
    },
    tags() {
      return this.tagsStore.allTags
    },
    loading() {
      return this.tagsStore.isLoading
    },
    totalTriggers() {
      return this.tagsStore.totalTriggers
    },
    averageTriggers() {
      return this.tagsStore.averageTriggers
    },
    recentTags() {
      return this.tags.slice(0, 5)
    }
  },
  mounted() {
    this.tagsStore.fetchTags()
  }
}
</script>


